import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plane, Building2, Bus, List, Plus, Pencil, Trash2 } from "lucide-react";
import client from "@/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KIND_LABEL = { markup: "Markup Group", block: "Block Group", discount: "Commission Group" };
const KIND_TINT = { markup: "bg-[#17A2B8]", block: "bg-[#DC3545]", discount: "bg-[#8E1A9E]" };
const PRODUCT = { flight: ["Flight", Plane], hotel: ["Hotel", Building2], bus: ["Bus", Bus] };

const GroupBtn = ({ product, kind, count, onOpen }) => (
  <button onClick={() => onOpen(product, kind)} data-testid={`mk-open-${product}-${kind}`}
    className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 ${KIND_TINT[kind]}`}>
    <List className="h-3.5 w-3.5" />{KIND_LABEL[kind]}{count !== undefined && ` (${count})`}
  </button>
);

const Banner = ({ product }) => {
  const [label, Icon] = PRODUCT[product];
  return (
    <div className="my-6 flex items-center justify-center gap-2 bg-gradient-to-r from-transparent via-white to-transparent py-4">
      <Icon className="h-8 w-8 fill-[#1B3FA0] text-[#1B3FA0]" />
      <h2 className="text-2xl font-bold tracking-tight text-[#1B3FA0] md:text-3xl">{label} Markup Setting</h2>
    </div>
  );
};

const SettingTable = ({ cols }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[560px] border border-[#1B3FA0]/40 bg-white">
      <thead><tr>{cols.map((c) => <th key={c[0]} className="border border-[#1B3FA0]/40 px-3 py-3 text-left text-lg font-semibold text-slate-900">{c[0]}</th>)}</tr></thead>
      <tbody><tr>{cols.map((c) => <td key={c[0]} className="border border-[#1B3FA0]/40 px-3 py-3">{c[1]}</td>)}</tr></tbody>
    </table>
  </div>
);

const NotFound = ({ id }) => <span className="inline-block rounded border border-slate-500 px-2.5 py-1 text-xs text-slate-700" data-testid={id}>Not Found</span>;

export const MarkupOverview = ({ onOpen }) => {
  const [counts, setCounts] = useState({});
  useEffect(() => { client.get("/admin/markup-groups").then((r) => setCounts(r.data.counts)); }, []);
  const c = (p, k) => counts[`${p}_${k}`] || 0;
  const b = (p, k) => <GroupBtn product={p} kind={k} count={c(p, k)} onOpen={onOpen} />;
  return (
    <div className="rounded-lg bg-[#EEF0F4] px-4 pb-8 md:px-8" data-testid="markup-overview">
      <Banner product="flight" />
      <SettingTable cols={[["Markup", b("flight", "markup")], ["Block Flights", b("flight", "block")], ["Instant Discount", b("flight", "discount")]]} />
      <Banner product="hotel" />
      <SettingTable cols={[["Markup", b("hotel", "markup")], ["Instant Discount", b("hotel", "discount")]]} />
      <Banner product="bus" />
      <SettingTable cols={[["Markup Manage", b("bus", "markup")], ["Instant Discount", b("bus", "discount")],
        ["Markup", c("bus", "markup") ? `${c("bus", "markup")} active` : <NotFound id="bus-markup-notfound" />],
        ["Commission", c("bus", "discount") ? `${c("bus", "discount")} active` : <NotFound id="bus-commission-notfound" />]]} />
    </div>
  );
};

const blank = (product, kind) => ({ name: "", product, kind, airlines: [], type: "flat", value: 0, active: true });

const GroupEditor = ({ value, onClose, onSaved }) => {
  const [g, setG] = useState({ ...value, airlinesText: (value.airlines || []).join(", ") });
  const save = async () => {
    const body = { name: g.name, product: g.product, kind: g.kind, type: g.type, value: Number(g.value) || 0, active: g.active,
      airlines: g.airlinesText.split(/[,\s]+/).filter(Boolean) };
    try {
      if (value.group_id) await client.put(`/admin/markup-groups/${value.group_id}`, body);
      else await client.post("/admin/markup-groups", body);
      toast.success("Group saved — prices updated live"); onSaved();
    } catch (x) { toast.error(typeof x?.response?.data?.detail === "string" ? x.response.data.detail : "Please check the values"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="mk-group-editor">
        <DialogHeader><DialogTitle>{value.group_id ? "Edit" : "New"} {PRODUCT[g.product][0]} {KIND_LABEL[g.kind]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Group name" value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} data-testid="mk-group-name" />
          {g.product === "flight" && (
            <Input placeholder={g.kind === "block" ? "Airline codes to block e.g. SG, I5" : "Airline codes (blank = all airlines)"}
              value={g.airlinesText} onChange={(e) => setG({ ...g, airlinesText: e.target.value.toUpperCase() })} data-testid="mk-group-airlines" />
          )}
          {g.kind !== "block" && (
            <div className="flex gap-2">
              <select value={g.type} onChange={(e) => setG({ ...g, type: e.target.value })} className="rounded-md border px-3 text-sm" data-testid="mk-group-type">
                <option value="flat">Flat ₹</option><option value="percent">Percent %</option>
              </select>
              <Input type="number" min="0" value={g.value} onChange={(e) => setG({ ...g, value: e.target.value })} data-testid="mk-group-value" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm"><Switch checked={g.active} onCheckedChange={(v) => setG({ ...g, active: v })} data-testid="mk-group-active" />Active</label>
          <p className="text-xs text-slate-500">
            {g.kind === "markup" && "Added to prices shown to B2C customers on FLYTRIP (agents always see net)."}
            {g.kind === "block" && "These airlines are hidden from all flight results on FLYTRIP."}
            {g.kind === "discount" && "Instant discount for B2C customers (capped at 50% of fare). TripJack is still charged the full net fare."}
          </p>
          <Button onClick={save} className="w-full bg-[#1B1F5B] hover:bg-[#141844]" data-testid="mk-group-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const GroupManager = ({ product, kind: kindProp }) => {
  const [kind, setKind] = useState(kindProp || "markup");
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const kinds = product === "flight" ? ["markup", "block", "discount"] : ["markup", "discount"];
  const load = () => client.get("/admin/markup-groups", { params: { product, kind } }).then((r) => setItems(r.data.groups));
  useEffect(() => { load(); }, [product, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (kindProp) setKind(kindProp); }, [kindProp]);
  const del = async (g) => { if (window.confirm(`Delete "${g.name}"?`)) { await client.delete(`/admin/markup-groups/${g.group_id}`); load(); } };
  return (
    <div data-testid={`mk-manager-${product}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#1B1F5B]">{PRODUCT[product][0]} Setting</h2>
        <div className="flex flex-wrap gap-2">
          {kinds.map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`mk-kind-${k}`}
              className={`rounded px-3 py-1.5 text-xs font-bold ${kind === k ? `${KIND_TINT[k]} text-white` : "border bg-white text-slate-600"}`}>{KIND_LABEL[k]}</button>
          ))}
          <Button size="sm" onClick={() => setEdit(blank(product, kind))} className="bg-[#0E7C6B] hover:bg-[#0A5348]" data-testid="mk-add-group"><Plus className="mr-1 h-4 w-4" />Add {KIND_LABEL[kind]}</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm" data-testid="mk-groups-table">
          <thead><tr className="bg-[#1B1F5B] text-left text-white">{["S.No", "Group Name", "Airlines", "Value", "Status", "Action"].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No {KIND_LABEL[kind]} yet</td></tr>}
            {items.map((g, i) => (
              <tr key={g.group_id} className="border-b" data-testid={`mk-group-row-${g.group_id}`}>
                <td className="px-3 py-2">{i + 1}</td><td className="px-3 py-2 font-semibold">{g.name}</td>
                <td className="px-3 py-2">{g.airlines?.length ? g.airlines.join(", ") : product === "flight" ? "All" : "—"}</td>
                <td className="px-3 py-2">{g.kind === "block" ? "Blocked" : g.type === "percent" ? `${g.value}%` : `₹${g.value}`}</td>
                <td className="px-3 py-2">{g.active ? <span className="text-[#0E7C6B]">Active</span> : <span className="text-slate-400">Inactive</span>}</td>
                <td className="px-3 py-2">
                  <button onClick={() => setEdit(g)} className="mr-2 text-[#1B1F5B]" data-testid={`mk-group-edit-${g.group_id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(g)} className="text-red-500" data-testid={`mk-group-delete-${g.group_id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <GroupEditor value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};
